<template>
  <section>
    <errors-list :errorMessage="errorMessage" />

    <v-data-table
      :headers="translatedHeaders"
      :items="items"
      :loading="loading"
      :rows-per-page-items="rowsPerPageItems"
      :total-items="pagination.totalItems"
      :pagination.sync="pagination"
    >
      <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
      <template slot="items" slot-scope="props">
        <tr>
          <template v-for="(header, index) in translatedHeaders">
            <td :key="index" v-if="header.value === 'download'" class="justify-end layout">
              <v-btn
                :href="`/api/repayment-files/${props.item.id}/download`"
                target="_blank"
                :outline="true"
                color="green darken-3"
                :small="true"
              >
                {{ $t('@repaymentsFileList/download') }}
              </v-btn>
            </td>

            <grid-cell
              v-else
              :header="header"
              :row="props.item"
              :key="index"
              @action="openRepaymentFileDialog(props.item)"
            ></grid-cell>
          </template>
        </tr>
      </template>
    </v-data-table>

    <v-dialog v-model="showDialog" lazy full-width width="1000px">
      <v-layout row justify-center align-center>
        <v-flex xs12>
          <v-card>
            <v-data-table
              :headers="repaymentFileDialogHeaders"
              :items="repaymentFileItems"
              :pagination="{ rowsPerPage: -1 }"
              hide-actions
              single-select
            >
              <template slot="items" slot-scope="props">
                <tr>
                  <template v-for="(header, index) in repaymentFileDialogHeaders">
                    <td :key="index" v-if="header.value === 'contractInternalId'">
                      <v-btn
                        :href="`/contract/${props.item.insurance.internalId}`"
                        target="_blank"
                        :outline="true"
                        color="green darken-3"
                        :small="true"
                      >
                        {{ props.item.insurance.internalId }}
                      </v-btn>
                    </td>

                    <grid-cell v-else :header="header" :row="props.item" :key="index"></grid-cell>
                  </template>
                </tr>
              </template>
            </v-data-table>

            <v-layout row>
              <v-flex xs12>
                <v-btn @click="closeRepaymentFileDialog" class="right">
                  {{ $t('@repaymentsFileList/close') }}
                </v-btn>
              </v-flex>
            </v-layout>
          </v-card>
        </v-flex>
      </v-layout>
    </v-dialog>
  </section>
</template>

<script>
import moment from 'moment';
import ErrorsList from '../../components/ErrorsList';
import GridCell from '../../components/GridCell';
import { getErrorMessage } from '../../errorHandlers';
import { createdBy, defaultDateTimeFormatter, serializeQuery } from '../../utils';

import { defaultHeaders as repaymentListHeaders } from './RepaymentList.vue';

export const defaultHeaders = [
  { text: '@repaymentsFileList/idColumnHeader', value: 'id', sortable: false },
  {
    text: '@repaymentsFileList/createdAtColumnHeader',
    value: 'createdAt',
    format: defaultDateTimeFormatter,
    sortable: true,
  },
  { text: '@repaymentsFileList/filenameColumnHeader', value: 'filename', sortable: false },
  {
    text: '@repaymentsFileList/downloadedColumnHeader',
    align: 'left',
    value: 'downloaded',
    sortable: false,
  },
  { text: '@repaymentsFileList/sumColumnHeader', value: 'sum', sortable: false },
  {
    text: '@repaymentsFileList/numRepaymentsColumnHeader',
    value: 'numRepayments',
    sortable: false,
  },
  {
    text: '@repaymentsFileList/byEmployeeColumnHeader',
    value: 'byEmployee',
    format: createdBy,
    sortable: false,
  },
  {
    text: '',
    align: 'right',
    sortable: false,
    value: 'download',
  },
];

export default {
  components: { GridCell, ErrorsList },

  data() {
    return {
      items: [],
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 10,
        sortBy: 'createdAt',
        totalItems: 0,
      },
      rowsPerPageItems: [10, 20, 30],
      selectedRepaymentFile: null,
      repaymentFileItems: [],
      loading: false,
      errorMessage: '',
      showDialog: false,
      statuses: {
        created: this.$t('@repaymentFileStatus/created'),
        downloaded: this.$t('@repaymentFileStatus/downloaded'),
      },
      repaymentStatuses: {
        pending: this.$t('@repaymentStatus/pending'),
        repaid: this.$t('@repaymentStatus/repaid'),
        completed: this.$t('@repaymentStatus/completed'),
      },
    };
  },

  watch: {
    pagination() {
      this.fetchData();
    },

    async selectedRepaymentFile() {
      if (this.selectedRepaymentFile) {
        const id = this.selectedRepaymentFile.id;

        const {
          data: { items },
        } = await this.$http.get(`/api/repayment-files/${id}/repayments`);

        const mappedItems = items.map((item) => ({
          ...item,
          status: this.repaymentStatuses[item.status],
        }));

        this.repaymentFileItems = mappedItems;
      } else {
        this.repaymentFileItems = [];
      }
    },

    showDialog() {
      if (!this.showDialog) {
        this.repaymentFileItems = [];
        this.selectedRepaymentFile = null;
      }
    },
  },

  computed: {
    translatedHeaders() {
      return defaultHeaders.map((h) => ({
        ...h,
        text: this.$t(h.text),
      }));
    },

    repaymentFileDialogHeaders() {
      return repaymentListHeaders
        .filter((h) => {
          return h.value !== 'selected';
        })
        .map((h) => ({
          ...h,
          text: this.$t(h.text),
        }));
    },

    query() {
      const skip = (this.pagination.page - 1) * this.pagination.rowsPerPage;
      const take = this.pagination.rowsPerPage;
      const query = {
        //...this.filters,
        skip,
        take,
      };

      if (this.pagination.sortBy) {
        query.sortBy = `${this.pagination.sortBy}`;
        query.sortOrder = this.pagination.descending ? 'DESC' : 'ASC';
      } else {
        delete query.sortBy;
        delete query.sortOrder;
      }

      if (!query.from) {
        query.from = moment()
          .startOf('month')
          .startOf('day')
          .format();
      }
      return query;
    },
  },

  methods: {
    openRepaymentFileDialog(row) {
      this.selectedRepaymentFile = row;
      this.showDialog = true;
    },

    closeRepaymentFileDialog() {
      this.showDialog = false;
      this.selectedRepaymentFile = null;
    },

    async fetchData() {
      const qs = serializeQuery(this.query);
      if (this.lastQueryString === qs || this.loading) {
        return;
      }

      try {
        this.loading = true;

        const {
          data: {
            items,
            meta: { total },
          },
        } = await this.$http.get(`/api/repayment-files?${qs}`);

        const withTranslatedStatuses = items.map((item) => ({
          ...item,
          status: this.statuses[item.status],
        }));

        this.items = withTranslatedStatuses;
        this.pagination.totalItems = total;
        this.errorMessage = '';
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
